import React, { useState } from 'react';
import { useFutureOrderStore } from '@components/Stores/FutureOrdersStore';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { useAccountStore } from '@components/Stores/accountStore';
import { useFutureOrders } from '@hooks/useFutureOrders';
import { useInventory } from '@hooks/useInventory';
import { useInventoryReports } from '@hooks/useInventoryReports';
import { useNavigate } from 'react-router-dom';
import { triggerExposureCalc, useExposure } from '../../hooks/useExposure';
import { useExposureCalculation } from '../../hooks/useExposureCalculation';
import { exposureBreakdownToMaterialExposure, calculateNetExposureValue } from '../../utils/exposureAdapter';
import { aggregateFutureOrdersToMaterialsDic, dictionaryToStackChart } from '../FutureOrder/FutureOrders/Utils';
import { ExposureCard } from './ExposureCard';
import { SkeletonDashboard } from './SkeletonDashboard';
import { StackedBarDoughnutChartCard } from './StackedBarDoughnutChartCard';
import { InventoryChartCard } from './dashboard-components/InventoryChartCard';
import { getExposureCardData, getFormulaToAccountById } from './dashboardUtils';
import { transformHedgesToStackedBar } from './hedgesAdapter';
import { transformSupplyToStackedBar } from './supplyAdapter';
import { transformDemandToStackedBar } from './demandChartAdapter';
import { exportDashboardDataToCSV } from './exportDashboardData';
import { GENERAL_ID, useExposureStore } from '../../components/Stores/exposureDataStore';
import {
  ADD_FO_FIRST_STEP,
  ADD_FUTURE_ORDER_DEFAULT_ORDER_ID,
  ADD_FUTURE_ORDER_ROOT_ROUTE,
} from '../FutureOrder/AddNewFutureOrder/AddNewFutureOrderUtils';
import { useFinancialPositions } from '@hooks/useFinancialPositions';
import { useDemandPositions } from '@hooks/useDemandPositions';
import { useSupplyPositions } from '@hooks/useSupplyPositions';
import { Download, Info } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { isSuperAdmin } from '../../sp-types/userType';
import { ExposureHorizonBanner } from './ExposureHorizonBanner';
import { getExposureWindow, countOutOfScope, formatHorizonDisplay } from '../../utils/exposureHorizon';
import { transformDemandPositionsToFutureOrders } from '../FutureOrder/SalesCommComponents/demandAdapter';
import { ExposureDetailsPopup } from './dashboard-components/ExposureDetailsPopup';
import { useFormulasPrices } from '@hooks/useFormulasPrices';
import { useFx } from '@hooks/useFx';
import { useBaseCurrency } from '@hooks/useBaseCurrency';
import { OverlayDisplayDiv } from '../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { ChartDetailsDialog } from './dashboard-components/ChartDetailsDialog';
import { useUser } from '@hooks/useUser';
import { useCategoryTerms } from '../../utils/categoryTerms';
import { useCardBar } from '../../components/FormulaCardBar/useCardBar';
import { useGeneralRisk } from '../../hooks/useGeneralRisk';
import { StrategyAlertsList } from '../../components/Notifications/StrategyAlertsList';
import { useWatchRules, extractPolicyConfig } from '../../hooks/useWatchRules';
import type { IRiskTolerance } from '../../sp-types/accountTypes';

export default function Dashboard() {
  const { account, formulaColorDictionary: colorsMap } = useAccountStore();
  const terms = useCategoryTerms();

  // Configure shared formula card bar for this page
  useCardBar({ filterWatchOnly: true });
  const { data: user } = useUser();

  const { data: inventoryData, refetch: fetchOldInventory } = useInventory();

  const { data: demandPositions, refetch: fetchDemand } = useDemandPositions();
  const { data: supplyPositions, refetch: fetchSupply } = useSupplyPositions();

  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear() || 2025);
  const { data: inventoryReports, refetch: fetchInventory } = useInventoryReports();

  const [editable, setEditable] = useState<boolean>(!!user?.isEditEnabled);

  React.useEffect(() => {
    if (user) {
      setEditable(!!user.isEditEnabled);
    }
  }, [user]);

  const { materialsMap } = useMaterialsStore();
  const baseCurrency = useBaseCurrency();
  const { getRateToBase } = useFx(baseCurrency);
  
  // Use latest spot prices from useFormulasPrices for chart calculations (no legacy fallback)
  const { data: formulasPricesData } = useFormulasPrices();
  const latestPricesMap: Record<string, number> = React.useMemo(() => {
    if (!formulasPricesData?.pricesMap) return {};
    // Convert FormulaPriceInfo to simple price map (use FX-converted price in base currency)
    const formulaPrices = formulasPricesData.pricesMap;
    return Object.fromEntries(
      Object.keys(formulaPrices).map((key) => [key, formulaPrices[key].price])
    );
  }, [formulasPricesData?.pricesMap]);

  const { refetch: fetchFutureOrders } = useFutureOrders();
  const { allFutureOrders } = useFutureOrderStore();
  const navigate = useNavigate();
  const { currentSelectedFormulaId, isNetExposureLoading, netExposure, setCurrentSelectedFormulaId } =
    useExposureStore();
  
  // Fetch hedges with full derivation (same model as Hedges page)
  const { data: hedgePositions, refetch: fetchHedges } = useFinancialPositions();
  const { refetch: refetchExposure } = useExposure(account.id);
  
  // NEW: Calculate exposure on-demand from Postgres data
  const { exposure: calculatedExposure, exposureByFormula, isLoading: isExposureCalculating } = useExposureCalculation({
    formulaId: currentSelectedFormulaId === GENERAL_ID ? undefined : currentSelectedFormulaId,
    useHorizon: true,
  });

  // Log calculated exposure for debugging
  React.useEffect(() => {
    if (calculatedExposure) {
      const formulaToAccount = getFormulaToAccountById(account, currentSelectedFormulaId || 'general');
      console.log('[Dashboard] Calculated Exposure:', {
        formulaId: currentSelectedFormulaId,
        demand: calculatedExposure.demand,
        supply: calculatedExposure.supply,
        hedges: calculatedExposure.hedges,
        netExposure: calculatedExposure.netExposure,
      });
      console.log('[Dashboard] Risk Tolerance:', {
        formulaToAccount,
        riskTolerance: formulaToAccount?.RiskTolerance,
      });
    }
  }, [calculatedExposure, currentSelectedFormulaId, account]);

  // Exposure-weighted general σ and μ — shared hook used by both Dashboard and Strategy
  const generalRisk = useGeneralRisk();

  // ─── Watch Rules → exposure thresholds (single source of truth) ───
  const { data: watchRules = [] } = useWatchRules(account?.id, { category: 'strategy' });

  const numericFormulaId = React.useMemo(() => {
    if (!currentSelectedFormulaId || currentSelectedFormulaId === GENERAL_ID) return null;
    const fa = account.formulasToAccount.find(f => f.formulaId === currentSelectedFormulaId);
    return fa?.id ? Number(fa.id) : null;
  }, [currentSelectedFormulaId, account.formulasToAccount]);

  const exposureRiskTolerance: IRiskTolerance = React.useMemo(() => {
    const DEFAULT_RT: IRiskTolerance = { dangerMinus: -5000000, warnMinus: -1000000, warnPlus: 1000000, dangerPlus: 5000000 };
    const isValid = (rt: IRiskTolerance) => rt.dangerMinus < rt.warnMinus && rt.warnMinus < rt.warnPlus && rt.warnPlus < rt.dangerPlus;

    if (!watchRules.length) {
      const old = getFormulaToAccountById(account, currentSelectedFormulaId || 'general').RiskTolerance;
      return isValid(old) ? old : DEFAULT_RT;
    }
    const et = extractPolicyConfig(watchRules, numericFormulaId).exposureThresholds;
    if (!et.dangerLow && !et.warnLow && !et.warnHigh && !et.dangerHigh) {
      const old = getFormulaToAccountById(account, currentSelectedFormulaId || 'general').RiskTolerance;
      return isValid(old) ? old : DEFAULT_RT;
    }
    return {
      dangerMinus: -(Math.abs(et.dangerLow)),
      warnMinus: -(Math.abs(et.warnLow)),
      warnPlus: et.warnHigh,
      dangerPlus: et.dangerHigh,
    };
  }, [watchRules, numericFormulaId, account, currentSelectedFormulaId]);

  const [terminals, setTerminals] = useState<number[]>([]);
  const [isTerminals, setIsTerminals] = useState<boolean>(false);
  const [isQuantity, setIsQuantity] = useState<boolean>(false);

  const setMaterial = (formulaId: string) => {
    setCurrentSelectedFormulaId(formulaId);

    // Get terminals from account formulas data (comes from quote_source_configs.source_config.months)
    const formulaToAccount = account?.formulasToAccount?.find(f => f.formulaId === formulaId);
    const terminalsValue = formulaId === GENERAL_ID ? [] : (formulaToAccount?.terminals || []);
    console.log('[Dashboard] Setting terminals for formula:', {
      formulaId,
      formulaToAccount,
      terminalsValue,
      allFormulas: account?.formulasToAccount
    });
    setTerminals(terminalsValue);
    
    if (formulaId === GENERAL_ID) {
      setIsQuantity(false);
    }
  };

  // Reset qty mode when switching to general (FormulaCardBar sets store directly, bypassing setMaterial)
  React.useEffect(() => {
    if (currentSelectedFormulaId === GENERAL_ID) {
      setIsQuantity(false);
    }
  }, [currentSelectedFormulaId]);

  React.useEffect(() => {
    if (account?.id) {
      // Only set to GENERAL_ID if no material is currently selected
      if (!currentSelectedFormulaId || currentSelectedFormulaId === '') {
        setMaterial(GENERAL_ID);
      }

      refetchExposure();
      fetchInventory();
      fetchOldInventory();
      fetchDemand();
      fetchSupply();
      fetchHedges();
      fetchFutureOrders();
    }
  }, [account?.id]);

  if (account?.skipDashboard) {
    navigate(account.skipDashboard);
  }

  const [isRefetchingExposure, setIsRefetchingExposure] = useState(false);

  const handleUpdateExposure = async (accountId: string, formulaId: string[]) => {
    setIsRefetchingExposure(true);
    try {
      await triggerExposureCalc({ accountId, materialIds: formulaId });
    } catch (error) {
      console.error('Error updating exposure:', error);
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
    await refetchExposure();
    setIsRefetchingExposure(false);
  };

  const getDateModifiedYear = (newYear: number): Date => {
    const currentDate = new Date();
    currentDate.setFullYear(newYear);
    return currentDate;
  };

  const horizonMonths = account?.viewingHorizonMonths || 12;

  // Both accumulated and monthly use same data now - hedges don't spread across months
  const memoAccumulatePositions = React.useMemo(
    () => transformHedgesToStackedBar(hedgePositions, currentSelectedFormulaId, materialsMap, colorsMap, isQuantity, latestPricesMap, horizonMonths),
    [hedgePositions, currentSelectedFormulaId, materialsMap, colorsMap, isQuantity, latestPricesMap, horizonMonths],
  );

  const memoMonthlyPositions = React.useMemo(
    () => transformHedgesToStackedBar(hedgePositions, currentSelectedFormulaId, materialsMap, colorsMap, isQuantity, latestPricesMap, horizonMonths),
    [hedgePositions, currentSelectedFormulaId, materialsMap, colorsMap, isQuantity, latestPricesMap, horizonMonths],
  );


  const memoInvReports = React.useMemo(() => inventoryReports, [inventoryReports]);

  // Ratio PO quantity (excluded from charts, shown as extra info)
  const ratioPoQty = React.useMemo(() => {
    if (!supplyPositions) return 0;
    return supplyPositions
      .filter(s => s.supply_type === 'ratio_po')
      .filter(s => currentSelectedFormulaId === GENERAL_ID || s.formula_code === currentSelectedFormulaId)
      .reduce((sum, s) => sum + Number(s.quantity || 0), 0);
  }, [supplyPositions, currentSelectedFormulaId]);

  // Supply data for bar chart (monthly breakdown)
  const memoSupplyMonthly = React.useMemo(
    () => transformSupplyToStackedBar(
      supplyPositions,
      inventoryReports || undefined,
      currentSelectedFormulaId,
      materialsMap,
      colorsMap,
      latestPricesMap,
      isQuantity,
      horizonMonths,
      getRateToBase,
      baseCurrency,
    ),
    [supplyPositions, inventoryReports, currentSelectedFormulaId, materialsMap, colorsMap, latestPricesMap, isQuantity, horizonMonths, getRateToBase, baseCurrency],
  );

  // Supply data for donut chart (accumulated totals)
  const memoSupplyAccumulated = React.useMemo(
    () => transformSupplyToStackedBar(
      supplyPositions,
      inventoryReports || undefined,
      currentSelectedFormulaId,
      materialsMap,
      colorsMap,
      latestPricesMap,
      isQuantity,
      horizonMonths,
      getRateToBase,
      baseCurrency,
    ),
    [supplyPositions, inventoryReports, currentSelectedFormulaId, materialsMap, colorsMap, latestPricesMap, isQuantity, horizonMonths, getRateToBase, baseCurrency],
  );

  // Demand data for bar chart (monthly breakdown)
  const memoFutureOrders = React.useMemo(
    () => transformDemandToStackedBar(
      demandPositions,
      currentSelectedFormulaId,
      materialsMap,
      colorsMap,
      latestPricesMap,
      isQuantity,
      horizonMonths,
      getRateToBase,
      baseCurrency,
    ),
    [demandPositions, currentSelectedFormulaId, materialsMap, colorsMap, latestPricesMap, isQuantity, horizonMonths, getRateToBase, baseCurrency],
  );

  // Demand data for donut chart (accumulated totals)
  const memoFutureOrdersAcumulated = React.useMemo(
    () => transformDemandToStackedBar(
      demandPositions,
      currentSelectedFormulaId,
      materialsMap,
      colorsMap,
      latestPricesMap,
      isQuantity,
      horizonMonths,
      getRateToBase,
      baseCurrency,
    ),
    [demandPositions, currentSelectedFormulaId, materialsMap, colorsMap, latestPricesMap, isQuantity, horizonMonths, getRateToBase, baseCurrency],
  );

  // Calculate total across all formulas for proportion display
  const allFormulasDemandTotal = React.useMemo(() => {
    if (currentSelectedFormulaId === GENERAL_ID) return undefined;
    const allFormulasData = transformDemandToStackedBar(
      demandPositions,
      GENERAL_ID,
      materialsMap,
      colorsMap,
      latestPricesMap,
      isQuantity,
      horizonMonths,
      getRateToBase,
      baseCurrency,
    );
    return allFormulasData.reduce((sum, item) => sum + (isQuantity ? (item.sumQuantity || 0) : (item.sumMonetary || 0)), 0);
  }, [demandPositions, currentSelectedFormulaId, materialsMap, colorsMap, latestPricesMap, isQuantity, horizonMonths, getRateToBase, baseCurrency]);

  const allFormulasSupplyTotal = React.useMemo(() => {
    if (currentSelectedFormulaId === GENERAL_ID) return undefined;
    const allFormulasData = transformSupplyToStackedBar(
      supplyPositions,
      inventoryReports || undefined,
      GENERAL_ID,
      materialsMap,
      colorsMap,
      latestPricesMap,
      isQuantity,
      horizonMonths,
      getRateToBase,
      baseCurrency,
    );
    return allFormulasData.reduce((sum, item) => sum + (isQuantity ? (item.sumQuantity || 0) : (item.sumMonetary || 0)), 0);
  }, [supplyPositions, inventoryReports, currentSelectedFormulaId, materialsMap, colorsMap, latestPricesMap, isQuantity, horizonMonths, getRateToBase, baseCurrency]);

  const allFormulasHedgesTotal = React.useMemo(() => {
    if (currentSelectedFormulaId === GENERAL_ID) return undefined;
    const allFormulasData = transformHedgesToStackedBar(
      hedgePositions,
      GENERAL_ID,
      materialsMap,
      colorsMap,
      isQuantity,
      latestPricesMap,
      horizonMonths,
    );
    return allFormulasData.reduce((sum, item) => sum + (isQuantity ? (item.sumQuantity || 0) : (item.sumMonetary || 0)), 0);
  }, [hedgePositions, currentSelectedFormulaId, materialsMap, colorsMap, latestPricesMap, isQuantity, horizonMonths]);

  const memoNetExposure = React.useMemo(() => netExposure, [netExposure, currentSelectedFormulaId]);
  const [showExpDetails, setShowExpDetails] = useState(false);
  const [showSalesDetails, setShowSalesDetails] = useState(false);
  const [showInventoryDetails, setShowInventoryDetails] = useState(false);

  const showSalesPopup = () => {
    setShowSalesDetails(true);
  };

  const [maxValues, setMaxValues] = React.useState<{ futureOrder: number; inventory: number; position: number }>({
    futureOrder: 0,
    inventory: 0,
    position: 0,
  });
  const setInventoryMax = React.useCallback(
    (inventory: number) => {
      if (inventory !== maxValues.inventory) setMaxValues((prev) => ({ ...prev, inventory }));
    },
    [maxValues.inventory],
  );
  const setOrdersMax = React.useCallback(
    (futureOrder: number) => {
      if (futureOrder !== maxValues.futureOrder) setMaxValues((prev) => ({ ...prev, futureOrder }));
    },
    [maxValues.futureOrder],
  );
  const setPositionMax = React.useCallback(
    (position: number) => {
      if (position !== maxValues.position) setMaxValues((prev) => ({ ...prev, position }));
    },
    [maxValues.position],
  );

  const [chartMax, setChartMax] = React.useState<number>(0);
  const [showProportion, setShowProportion] = React.useState<boolean>(true);
  React.useEffect(() => {
    const vals = [maxValues.futureOrder, maxValues.inventory, maxValues.position].filter(v => v > 0);
    if (vals.length > 0) {
      setChartMax(1.02 * Math.max(...vals));
    } else {
      setChartMax(0);
    }
  }, [maxValues.futureOrder, maxValues.inventory, maxValues.position]);

  const isSuperAdminUser = isSuperAdmin(user);

  const handleExportData = () => {
    exportDashboardDataToCSV({
      demand: memoFutureOrders || [],
      supply: memoSupplyMonthly || [],
      hedges: memoMonthlyPositions || [],
    });
  };

  // Calculate out-of-scope counts for transparency
  const exposureWindow = React.useMemo(() => getExposureWindow(horizonMonths), [horizonMonths]);
  
  const outOfScopeCounts = React.useMemo(() => {
    const demandOutOfScope = demandPositions ? countOutOfScope(
      demandPositions,
      (d) => {
        const [year, month] = d.delivery_period.split('-').map(Number);
        return { year, month };
      },
      exposureWindow
    ) : 0;

    const supplyOutOfScope = supplyPositions ? countOutOfScope(
      supplyPositions,
      (s) => {
        const [year, month] = s.delivery_or_available_period.split('-').map(Number);
        return { year, month };
      },
      exposureWindow
    ) : 0;

    const hedgesOutOfScope = hedgePositions ? countOutOfScope(
      hedgePositions,
      (h) => {
        const date = new Date(h.maturity_date);
        return { year: date.getFullYear(), month: date.getMonth() + 1 };
      },
      exposureWindow
    ) : 0;

    const counts = {
      demand: demandOutOfScope,
      supply: supplyOutOfScope,
      hedges: hedgesOutOfScope,
    };
    
    return counts;
  }, [demandPositions, supplyPositions, hedgePositions, exposureWindow]);

  return (
    <div className="flex flex-col flex-1 h-full  gap-3">
      <div className="flex flex-row flex-1 space-x-4 overflow-hidden   ">
        <div className="flex basis-5/6 overflow-hidden">
          <div className="flex-col flex w-full align-start space-y-4  ">
            {calculatedExposure ? (
              <div>
                <ExposureCard
                  mainTitle={
                    currentSelectedFormulaId === GENERAL_ID 
                      ? 'Overall Exposure' 
                      : account.formulasToAccount.find(f => f.formulaId === currentSelectedFormulaId)?.displayName 
                        ? `${account.formulasToAccount.find(f => f.formulaId === currentSelectedFormulaId)?.displayName} Exposure`
                        : 'Exposure'
                  }
                  exposureBreakdown={calculatedExposure}
                  isSpecificMode={currentSelectedFormulaId !== GENERAL_ID}
                  gaugeBarProps={{
                    riskTolerance: exposureRiskTolerance,
                    currentValue: calculateNetExposureValue(calculatedExposure),
                    formulaColor: currentSelectedFormulaId === GENERAL_ID
                      ? '#6b7280'
                      : colorsMap[currentSelectedFormulaId],
                  }}
                  netExposure={calculateNetExposureValue(calculatedExposure)}
                  totalAmounts={exposureBreakdownToMaterialExposure(calculatedExposure)}
                  riskPercent={currentSelectedFormulaId && currentSelectedFormulaId !== GENERAL_ID && materialsMap[currentSelectedFormulaId]
                    ? {
                        volatility: materialsMap[currentSelectedFormulaId].volatility ?? 0,
                        volatilityMean: materialsMap[currentSelectedFormulaId].volatilityMean,
                        riskBackTesting: materialsMap[currentSelectedFormulaId].riskBackTesting ?? [],
                      }
                    : {
                        ...account.riskPercents,
                        volatility: generalRisk.volatility,
                        volatilityMean: generalRisk.volatilityMean,
                      }
                  }
                  selectedMaterial={currentSelectedFormulaId}
                  material={materialsMap[currentSelectedFormulaId]}
                  showExposureDetails={['RFVCLLEUDC1', 'RFVCBLEUDC1'].includes(currentSelectedFormulaId)}
                  onExpDetailClk={() => setShowExpDetails(true)}
                  isExposureLoading={isExposureCalculating}
                  horizonMonths={horizonMonths}
                  outOfScopeCounts={outOfScopeCounts}
                />
              </div>
            ) : (
              <div className="flex flex-col grow w-full min-h-36">
                <div className="flex flex-grow animate-pulse bg-white"></div>
              </div>
            )}
            <div className=" flex flex-row  h-full  space-x-6 pb-2  justify-between overflow-hidden ">
              <div className="flex-1 bg-white shadow-md overflow-auto">
                {memoFutureOrders ? (
                  <StackedBarDoughnutChartCard
                    data={memoFutureOrders}
                    accumulated={memoFutureOrdersAcumulated}
                    cardTitle={account.titleOverrides?.['futureOrders'] || terms.demand}
                    cardDescription="What you promised to deliver to customers"
                    cardUrl={account.isSalesCommitment ? '/sales-commitments' : '/future-order'}
                    addNewUrl={
                      editable
                        ? `/${ADD_FUTURE_ORDER_ROOT_ROUTE}/${ADD_FUTURE_ORDER_DEFAULT_ORDER_ID}/${ADD_FO_FIRST_STEP}`
                        : undefined
                    }
                    currentDate={getDateModifiedYear(selectedYear)}
                    formulaId={currentSelectedFormulaId}
                    terminals={[]}
                    showDetails={showSalesPopup}
                    allFormulasTotal={allFormulasDemandTotal}
                    {...{
                      setIsTerminals,
                      isTerminals,
                      isQuantity,
                      setIsQuantity,
                      setMaxVal: setOrdersMax,
                      chartMax,
                      showProportion,
                    }}
                  />
                ) : (
                  <div className="flex w-full h-full bg-gray-100 opacity-20 animate-pulse"></div>
                )}
              </div>
              <div className="flex-1 bg-white shadow-md overflow-auto">
                {memoSupplyMonthly ? (
                  <StackedBarDoughnutChartCard
                    data={memoSupplyMonthly}
                    accumulated={memoSupplyAccumulated}
                    cardTitle={account.titleOverrides?.['inventory'] || terms.supply}
                    cardDescription="What you have in stock or purchased"
                    annotation={ratioPoQty > 0 ? `+ ${Math.round(ratioPoQty)} MT ratio POs (no fixed price)` : undefined}
                    cardUrl="/supply"
                    allFormulasTotal={allFormulasSupplyTotal}
                    addNewUrl={editable ? '/inventory/add' : undefined}
                    currentDate={getDateModifiedYear(selectedYear)}
                    formulaId={currentSelectedFormulaId}
                    terminals={[]}
                    showDetails={() => setShowInventoryDetails(true)}
                    {...{
                      setIsTerminals,
                      isTerminals,
                      isQuantity,
                      setIsQuantity,
                      setMaxVal: setInventoryMax,
                      chartMax,
                      showProportion,
                    }}
                  />
                ) : (
                  <div className="flex w-full h-full bg-gray-100 opacity-20 animate-pulse"></div>
                )}
              </div>

              <div className="flex-1 bg-white shadow-md overflow-auto">
                <StackedBarDoughnutChartCard
                  data={memoMonthlyPositions}
                  accumulated={memoAccumulatePositions}
                  cardTitle={terms.hedge}
                  cardDescription="Your insurance against price swings"
                  cardUrl="/hedges"
                  addNewUrl={editable ? '/hedges/add' : undefined}
                  currentDate={getDateModifiedYear(selectedYear)}
                  formulaId={currentSelectedFormulaId}
                  terminals={[]}
                  allFormulasTotal={allFormulasHedgesTotal}
                  {...{ setIsTerminals, isTerminals, isQuantity, setIsQuantity }}
                  {...{ setMaxVal: setPositionMax, chartMax, showProportion }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col flex-grow pb-2   basis-1/6 ">
          <div className="flex flex-1 flex-col h-full gap-4  ">
            {/* Chart display toggles */}
            <div className="flex items-center justify-center gap-3 pt-2">
              {/* Qty / Value toggle */}
              <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => setIsQuantity(true)}
                  disabled={currentSelectedFormulaId === GENERAL_ID}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors font-medium ${
                    currentSelectedFormulaId === GENERAL_ID
                      ? 'text-gray-400 cursor-not-allowed'
                      : isQuantity
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Qty
                </button>
                <button
                  onClick={() => setIsQuantity(false)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors font-medium ${
                    !isQuantity
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Value
                </button>
              </div>
              {/* Same Scale toggle */}
              <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => setShowProportion(!showProportion)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors font-medium ${
                    showProportion
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Scale
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <StrategyAlertsList colorsMap={colorsMap} />
            </div>
          </div>
        </div>
      </div>
      {showExpDetails && (
        <ExposureDetailsPopup
          close={() => setShowExpDetails(false)}
          formulaId={currentSelectedFormulaId}
          orders={memoFutureOrders}
          inventory={memoInvReports || []}
          positions={memoAccumulatePositions}
        />
      )}
      {showSalesDetails && (
        <OverlayDisplayDiv setDisplayOverlay={() => setShowSalesDetails(false)} hideClose={true}>
          {memoFutureOrders ? (
            <ChartDetailsDialog
              monthExposures={memoFutureOrders}
              accumulated={memoFutureOrdersAcumulated}
              cardTitle={account.titleOverrides?.['futureOrders'] || 'Sales commitments'}
              cardDescription="Sales commitments"
              cardUrl="/future-order"
              addNewUrl={`/${ADD_FUTURE_ORDER_ROOT_ROUTE}/${ADD_FUTURE_ORDER_DEFAULT_ORDER_ID}/${ADD_FO_FIRST_STEP}`}
              currentDate={getDateModifiedYear(selectedYear)}
              formulaId={currentSelectedFormulaId}
              {...{ setIsTerminals, isTerminals, terminals, isQuantity, setIsQuantity }}
              showPriceToggle={false}
              onClose={() => setShowSalesDetails(false)}
            />
          ) : (
            <div className="flex w-full h-full bg-gray-100 opacity-20 animate-pulse"></div>
          )}
        </OverlayDisplayDiv>
      )}
      {showInventoryDetails && (
        <OverlayDisplayDiv setDisplayOverlay={() => setShowInventoryDetails(false)} hideClose={true}>
          {memoSupplyMonthly ? (
            <ChartDetailsDialog
              monthExposures={memoSupplyMonthly}
              accumulated={memoSupplyAccumulated}
              cardTitle={account.titleOverrides?.['inventory'] || terms.supply}
              cardDescription="What you have in stock or purchased"
              cardUrl="/supply"
              currentDate={getDateModifiedYear(selectedYear)}
              formulaId={currentSelectedFormulaId}
              {...{ setIsTerminals, isTerminals, terminals, isQuantity, setIsQuantity }}
              showPriceToggle={true}
              onClose={() => setShowInventoryDetails(false)}
            />
          ) : (
            <div className="flex w-full h-full bg-gray-100 opacity-20 animate-pulse"></div>
          )}
        </OverlayDisplayDiv>
      )}
    </div>
  );
}
